/* @jsx h */
import { defineComponent, h, computed } from "vue";
import type { WalletPassDesign, WalletPassDesignDocument } from "../../types";
import { ColorField, PanelRow, PanelSection, TemplateInput } from "./primitives";

/** WCAG relative luminance, for the contrast guard below. */
const luminance = (hex: string): number => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!m) return 0;
  const channel = (v: string) => {
    const c = parseInt(v, 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return (
    0.2126 * channel(m[1]) + 0.7152 * channel(m[2]) + 0.0722 * channel(m[3])
  );
};

export const contrastRatio = (a: string, b: string): number => {
  const la = luminance(a);
  const lb = luminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
};

const PRESETS = [
  { name: "Dark", background: "#111827", foreground: "#FFFFFF", label: "#B4B4B4" },
  { name: "Blue", background: "#27457B", foreground: "#FFFFFF", label: "#C6D3EB" },
  { name: "Gold", background: "#7C5B12", foreground: "#FFF8E7", label: "#E4C883" },
  { name: "Green", background: "#0F5132", foreground: "#FFFFFF", label: "#A7D7BE" },
];

interface StylePanelProps {
  design: WalletPassDesign;
  onChange: (patch: Partial<WalletPassDesignDocument>) => void;
}

/** Brand: the colours and logo text every platform shares. */
export const StylePanel = defineComponent<StylePanelProps>({
  name: "StylePanel",
  props: ["design", "onChange"],
  setup(props) {
    const brand = () => props.design.document.brand;
    const patchBrand = (patch: Partial<WalletPassDesignDocument["brand"]>) =>
      props.onChange({ brand: { ...brand(), ...patch } });
    const patchColors = (
      patch: Partial<WalletPassDesignDocument["brand"]["colors"]>,
    ) => patchBrand({ colors: { ...brand().colors, ...patch } });

    const valueContrast = computed(() =>
      contrastRatio(brand().colors.foreground, brand().colors.background),
    );
    const labelContrast = computed(() =>
      contrastRatio(brand().colors.label, brand().colors.background),
    );

    return () => (
      <div class="flex flex-col gap-4">
        <PanelSection
          title="Preset"
          description="A starting point — every colour stays editable."
        >
          <div class="flex flex-wrap gap-2">
            {PRESETS.map((preset) => (
              <button
                key={preset.name}
                class="btn btn-sm btn-outline gap-2"
                onClick={() =>
                  patchColors({
                    background: preset.background,
                    foreground: preset.foreground,
                    label: preset.label,
                  })
                }
              >
                <span
                  class="w-4 h-4 rounded-full ring-1 ring-base-content/20"
                  style={{ backgroundColor: preset.background }}
                />
                {preset.name}
              </button>
            ))}
          </div>
        </PanelSection>

        <PanelSection
          title="Colours"
          description="Used by Apple Wallet, Google Wallet and the browser card."
        >
          <ColorField
            label="Background"
            value={brand().colors.background}
            onChange={(v) => patchColors({ background: v })}
          />
          <ColorField
            label="Field values"
            value={brand().colors.foreground}
            onChange={(v) => patchColors({ foreground: v })}
          />
          <ColorField
            label="Field labels"
            value={brand().colors.label}
            onChange={(v) => patchColors({ label: v })}
          />

          <div class="flex flex-col gap-2 pt-1">
            <div class="flex items-center justify-between text-xs">
              <span class="text-base-content/70">Value contrast</span>
              <span
                class={`badge badge-sm ${
                  valueContrast.value >= 4.5 ? "badge-success" : "badge-error"
                }`}
              >
                {valueContrast.value.toFixed(1)}:1
              </span>
            </div>
            <div class="flex items-center justify-between text-xs">
              <span class="text-base-content/70">Label contrast</span>
              <span
                class={`badge badge-sm ${
                  labelContrast.value >= 7
                    ? "badge-success"
                    : labelContrast.value >= 4.5
                      ? "badge-warning"
                      : "badge-error"
                }`}
              >
                {labelContrast.value.toFixed(1)}:1
              </span>
            </div>
          </div>
        </PanelSection>

        <PanelSection title="Logo text">
          <PanelRow
            label="Logo text"
            hint="Beside the logo on every platform. Usually the shop name."
          >
            <TemplateInput
              kind={props.design.kind}
              value={brand().logo_text}
              placeholder="{{restaurant.name}}"
              onChange={(v) => patchBrand({ logo_text: v })}
            />
          </PanelRow>
        </PanelSection>
      </div>
    );
  },
});
